import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RESOURCE_KEYS, type ResourceName } from '../services/resourceKeys.js';

/**
 * 161: the one way an admin form saves a record.
 *
 * Every save has to tell the cache what it changed, and before this hook that decision was made
 * independently at fourteen call sites — nine of them wrongly. Three screens invalidated the list
 * but never the record they had just written, so reopening the edit form served the value from
 * before the save; pressing Save on that form then wrote the old value back and silently reverted
 * the operator's work. Six more invalidated nothing at all.
 *
 * 🔴 `resource` is REQUIRED and its type is a closed union. That is the whole of FR-006: a form page
 * that does not say what it saves does not compile. The previous convention — remember to call
 * `invalidateQueries` with the right key — is what failed, and a comment restating it would fail
 * the same way. Feature 063 established this mechanism for navigation capabilities and proved it by
 * deletion; `tests/…` does the same here.
 *
 * Deliberately NOT a write-through cache. Putting the submitted values into the cache would remove
 * the round trip, and would also show the operator their own input as though the server had
 * accepted it — including the fields the server normalises, rejects or computes. The cache should
 * hold what the server holds.
 */
export function useSaveMutation<TInput = void, TResult = unknown>({
  resource,
  affects,
  id,
  mutationFn,
  onSaved,
  onError,
}: {
  /** Which resource this save affects. Required — see FR-006. */
  resource: ResourceName;
  /**
   * 189: OTHER resources this save invalidates, when its effect is not confined to one.
   *
   * 🔴 Rescheduling a booking moves a car's occupancy, so the fleet schedule goes stale as well —
   * a different resource with a different key. The two alternatives were both worse: invalidating by
   * hand at the call site is the per-call-site decision this hook exists to remove (nine of fourteen
   * sites got it wrong), and leaving the second resource stale shows the operator the pre-save day,
   * which is 161's defect in its original form. Still inside the closed union, so a resource that
   * does not exist does not compile.
   *
   * Only the LIST prefix of an affected resource is invalidated: prefix matching reaches every day
   * and filter under it, and a save has no way to know which record of another resource moved.
   */
  affects?: readonly ResourceName[];
  /** The record being updated, or omitted for a create, which has no record key yet. */
  id?: string;
  mutationFn: (input: TInput) => Promise<TResult>;
  /** Runs after invalidation. This is where a page navigates (FR-007). */
  onSaved?: (result: TResult) => void;
  onError?: (error: unknown) => void;
}): { save: (input: TInput) => Promise<void>; isPending: boolean; error: unknown } {
  const queryClient = useQueryClient();
  const mutation = useMutation({ mutationFn, onError });

  async function save(input: TInput): Promise<void> {
    let result: TResult;
    try {
      result = await mutation.mutateAsync(input);
    } catch {
      // 🔴 Deliberately does NOT re-throw. `onSaved` not running is the failure signal, and the
      // error itself is returned as `error` for the form to render — which is how these pages
      // already display it. Rejecting instead would oblige all nine call sites to attach a
      // `.catch`, and the one that forgot would produce an unhandled rejection rather than a
      // visible message.
      //
      // Nothing is invalidated on this path: the server changed nothing, and discarding good cache
      // entries would make a failure look like a change (FR-004).
      return;
    }

    const keys = RESOURCE_KEYS[resource];
    try {
      // The list prefix, so every page and filter combination of it goes stale at once.
      await queryClient.invalidateQueries({ queryKey: keys.list });
      // Separately, because a record key is not generally under its list prefix — `['car', id]`
      // does not begin with `['admin-cars']`. Relying on prefix matching here is the class A bug.
      if (id) await queryClient.invalidateQueries({ queryKey: keys.one(id) });
      // 189: and anything else this save made stale. Inside the same try, and after the two above,
      // for the same reason they are there — a failed refetch is a stale screen, not a failed save.
      for (const other of affects ?? []) {
        await queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS[other].list });
      }
    } catch {
      // 🔴 The write already reached the server. A refetch that fails afterwards is a stale screen,
      // which the screen's own error state handles; reporting it as a failed save would tell the
      // operator their change was lost when it was not. Swallowed on purpose.
    }

    // After invalidation, never before: onSaved is where the page navigates, and a list mounted
    // against an entry still considered fresh renders the value the save replaced (FR-007).
    onSaved?.(result);
  }

  return { save, isPending: mutation.isPending, error: mutation.error };
}
